import { useState } from "react";
import axios from "axios";
import { Loader2, CreditCard, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Physical Card order form.
 * Collects name-on-card + shipping address + minimal KYC. Submits to
 *   POST /api/terminal/physical-card
 * which stores the request and notifies the admin dashboard.
 */

const SA_CITIES = [
  "Johannesburg", "Cape Town", "Durban", "Pretoria", "Port Elizabeth",
  "Bloemfontein", "East London", "Polokwane", "Nelspruit", "Kimberley",
  "Rustenburg", "Stellenbosch", "Pietermaritzburg", "Sandton", "Centurion",
];

const ID_TYPES = [
  { v: "za_id", l: "South African ID" },
  { v: "passport", l: "Passport" },
  { v: "asylum", l: "Asylum / refugee document" },
];

export default function PhysicalCardOrder({ token, defaultName = "" }) {
  const [form, setForm] = useState({
    name_on_card: defaultName || "",
    full_legal_name: "",
    id_type: "za_id",
    id_number: "",
    phone: "",
    street: "",
    suburb: "",
    city: SA_CITIES[0],
    postal_code: "",
    province: "Gauteng",
    country: "South Africa",
  });
  const [idDoc, setIdDoc] = useState(null);
  const [poaDoc, setPoaDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validFile = (f) => {
    if (!f) return true;  // optional (backend will require them via admin review flow)
    if (f.size > 8 * 1024 * 1024) return false;
    return ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "application/pdf"].includes(f.type);
  };

  const valid = form.name_on_card.trim().length >= 2
    && form.full_legal_name.trim().length >= 3
    && form.id_number.trim().length >= 5
    && form.phone.trim().length >= 8
    && form.street.trim().length >= 4
    && form.city.trim().length >= 2
    && form.postal_code.trim().length >= 3
    && idDoc && poaDoc  // KYC docs required
    && validFile(idDoc) && validFile(poaDoc);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (idDoc) fd.append("id_document", idDoc);
      if (poaDoc) fd.append("proof_of_address", poaDoc);
      const { data } = await axios.post(
        `${API}/terminal/physical-card`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      setMsg(`Request received (${data.order_id}). Concierge will confirm within 24h.`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const field = (label, name, inputProps = {}) => (
    <label className="block">
      <span className="mb-1 block text-[9px] uppercase tracking-[0.28em] text-white/45">{label}</span>
      <input
        data-testid={`pc-${name}`}
        value={form[name]}
        onChange={set(name)}
        className="w-full border border-white/15 bg-ink px-3 py-2.5 font-tabular text-xs tracking-wide text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
        {...inputProps}
      />
    </label>
  );

  return (
    <div className="border border-gold/20 bg-surface p-6" data-testid="physical-card-order">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-md border border-gold/30 bg-gold/[0.08]">
          <CreditCard className="h-4 w-4 text-gold" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Physical card · concierge order</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Verified courier · 5–10 business days</p>
        </div>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="pc-form">
        {field("Name on card", "name_on_card", { placeholder: "As it should appear", maxLength: 26 })}
        {field("Full legal name", "full_legal_name", { placeholder: "For KYC verification" })}

        <label className="block">
          <span className="mb-1 block text-[9px] uppercase tracking-[0.28em] text-white/45">ID document type</span>
          <select
            data-testid="pc-id_type"
            value={form.id_type}
            onChange={set("id_type")}
            className="w-full border border-white/15 bg-ink px-3 py-2.5 font-tabular text-xs tracking-wide text-white focus:border-gold focus:outline-none"
          >
            {ID_TYPES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </label>
        {field("ID / passport number", "id_number", { placeholder: "e.g. 900101 5009 08 3", maxLength: 30 })}

        {field("Mobile number", "phone", { placeholder: "+27 82 000 0000", type: "tel", maxLength: 20 })}
        {field("Postal code", "postal_code", { placeholder: "2196", maxLength: 8 })}

        <label className="block md:col-span-2">
          <span className="mb-1 block text-[9px] uppercase tracking-[0.28em] text-white/45">Street address</span>
          <input
            data-testid="pc-street"
            value={form.street}
            onChange={set("street")}
            placeholder="Unit, building, street"
            maxLength={80}
            className="w-full border border-white/15 bg-ink px-3 py-2.5 font-tabular text-xs tracking-wide text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
          />
        </label>

        {field("Suburb", "suburb", { placeholder: "e.g. Sandton" })}
        <label className="block">
          <span className="mb-1 block text-[9px] uppercase tracking-[0.28em] text-white/45">City</span>
          <select
            data-testid="pc-city"
            value={form.city}
            onChange={set("city")}
            className="w-full border border-white/15 bg-ink px-3 py-2.5 font-tabular text-xs tracking-wide text-white focus:border-gold focus:outline-none"
          >
            {SA_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        {field("Province", "province", { placeholder: "Gauteng, Western Cape…" })}
        {field("Country", "country", { placeholder: "South Africa" })}

        {/* KYC document uploads */}
        <label className="block md:col-span-2">
          <span className="mb-1 block text-[9px] uppercase tracking-[0.28em] text-white/45">
            ID document scan · PDF / JPG / PNG (max 8MB)
          </span>
          <input
            data-testid="pc-id_document"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
            onChange={(e) => setIdDoc(e.target.files?.[0] || null)}
            className="block w-full text-xs text-white/80 file:mr-3 file:cursor-pointer file:border-0 file:bg-gold/20 file:px-4 file:py-2 file:text-[10px] file:uppercase file:tracking-[0.2em] file:text-gold hover:file:bg-gold/30"
          />
          {idDoc && (
            <p className="mt-1 text-[10px] text-[#26A69A]" data-testid="pc-id_document-name">
              ✓ {idDoc.name} · {(idDoc.size / 1024).toFixed(0)} KB
            </p>
          )}
        </label>

        <label className="block md:col-span-2">
          <span className="mb-1 block text-[9px] uppercase tracking-[0.28em] text-white/45">
            Proof of address · PDF / JPG / PNG (max 8MB, {"<"} 3 months old)
          </span>
          <input
            data-testid="pc-proof_of_address"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
            onChange={(e) => setPoaDoc(e.target.files?.[0] || null)}
            className="block w-full text-xs text-white/80 file:mr-3 file:cursor-pointer file:border-0 file:bg-gold/20 file:px-4 file:py-2 file:text-[10px] file:uppercase file:tracking-[0.2em] file:text-gold hover:file:bg-gold/30"
          />
          {poaDoc && (
            <p className="mt-1 text-[10px] text-[#26A69A]" data-testid="pc-proof_of_address-name">
              ✓ {poaDoc.name} · {(poaDoc.size / 1024).toFixed(0)} KB
            </p>
          )}
        </label>

        <div className="md:col-span-2 mt-1 flex items-center justify-between">
          <p className="text-[10px] text-white/40">
            By submitting you consent to KYC verification per FICA regulations.
          </p>
          <button
            data-testid="pc-submit"
            type="submit"
            disabled={!valid || busy}
            className="rounded-full bg-gold px-6 py-3 text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-gold-light disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request physical card"}
          </button>
        </div>
      </form>

      {msg && (
        <p className="mt-4 flex items-center gap-2 text-xs text-[#26A69A]" data-testid="pc-success">
          <CheckCircle2 className="h-4 w-4" /> {msg}
        </p>
      )}
      {err && <p className="mt-4 text-xs text-[#FF003C]" data-testid="pc-error">{err}</p>}
    </div>
  );
}
